import { motion } from "framer-motion";
import { Shield, Layers, Activity, MessagesSquare } from "lucide-react";

const POINTS = [
  {
    icon: Layers,
    title: "Architecture-first",
    description:
      "We design for scale, security and integration before writing the first line of business logic.",
  },
  {
    icon: Shield,
    title: "Integration depth",
    description:
      "ERP, CRM, payments, tax, logistics, identity — we have done it, and we will do it for you.",
  },
  {
    icon: Activity,
    title: "Observability built-in",
    description:
      "Performance, errors, business KPIs — instrumented from day one, not bolted on later.",
  },
  {
    icon: MessagesSquare,
    title: "Clear communication",
    description:
      "You always know what we are building, why, and when it ships. Status is not a meeting.",
  },
];

const WhyUs = () => {
  return (
    <section id="why" data-testid="why-choose-us-section" className="section-pad relative border-t border-[rgba(244,234,222,0.08)]">
      <div className="container-narrow">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.45 }}
            className="lg:col-span-5"
          >
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#E94B4B]">
              — Why GREINCH
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#F4EADE]">
              You ask. <span className="text-coral">We deliver.</span>
            </h2>
            <p className="mt-5 text-base sm:text-lg text-[#EFE3D6]/80 leading-relaxed">
              We are an international B2B partner built around senior delivery teams,
              decisive product thinking and a trusted broker network. No silos, no hand-offs,
              no surprises — just commerce systems and partnerships that move.
            </p>
            <div className="mt-7 inline-flex items-center gap-3 rounded-xl border border-[rgba(244,234,222,0.14)] bg-[rgba(244,234,222,0.03)] px-4 py-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B9AFA6]">
                Partner of record
              </span>
              <span className="h-3 w-px bg-[rgba(244,234,222,0.18)]" />
              <span className="font-display text-sm text-[#F4EADE]">Senior. Accountable. Global.</span>
            </div>
          </motion.div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {POINTS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  data-testid={`why-card-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.45, delay: i * 0.06 }}
                  className="group rounded-2xl border border-[rgba(244,234,222,0.12)] bg-[rgba(17,16,21,0.7)] p-5 sm:p-6 hover:border-[rgba(244,234,222,0.24)] transition-colors"
                >
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#6B1F47]/25 ring-1 ring-[#6B1F47]/45 text-[#F4EADE] group-hover:text-[#E94B4B] transition-colors">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold text-[#F4EADE]">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm text-[#EFE3D6]/75 leading-relaxed">
                    {p.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyUs;
